import { createReducer, on, Action } from "@ngrx/store";
import { createEntityAdapter, EntityState } from "@ngrx/entity";
import { ITable } from "@common/models/table";
import { HttpErrorResponse } from "@angular/common/http";
import { IColumnDefinition } from "@common/models/column-definition";
import * as actions from "./table.actions";

export type State = EntityState<TableInstance>
export const tableAdapter = createEntityAdapter<TableInstance>();

export class TableInstance {
  id: string;

  table?: ITable;
  loadingTable?: boolean;

  columns?: IColumnDefinition[];
  loadingColumns?: boolean;
  allColumns?: IColumnDefinition[];
  loadingAllColumns?: boolean;
  settingSelectedColumns?: boolean;
  reorderingColumns?: boolean;
  resizingColumns?: boolean;
  resettingColumnsToDefault?: boolean;

  error?: Error | HttpErrorResponse | string;
}

export const initialState: State = tableAdapter.getInitialState();

export function reducer(state: State | undefined, action: Action): State {
  return tableReducer(state, action);
}

const tableReducer = createReducer(
  initialState,

  // table load handlers --------------------------------------------------

  on(actions.loadTable, (state, action) => {
    if (stateHasId(state, action.id)) {
      return tableAdapter.updateOne(
        { id: action.id, changes: { loadingTable: true } },
        state
      );
    }
    return tableAdapter.addOne(
      {
        id: action.id,
        loadingTable: true,
      },
      state
    );
  }),
  on(actions.loadTableComplete, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { table: action.table, loadingTable: false },
      },
      state
    );
  }),
  on(actions.loadTableError, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { error: action.error },
      },
      state
    );
  }),

  // available columns handlers -------------------------------------------

  on(actions.getAvailableColumns, (state, action) => {
    if (stateHasId(state, action.id)) {
      return tableAdapter.updateOne(
        { id: action.id, changes: { loadingColumns: true } },
        state
      );
    }
    return tableAdapter.addOne(
      {
        id: action.id,
        loadingColumns: true,
      },
      state
    );
  }),
  on(actions.getAvailableColumnsComplete, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { columns: action.columns, loadingColumns: false },
      },
      state
    );
  }),
  on(actions.getAvailableColumnsError, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { error: action.error },
      },
      state
    );
  }),

  // all columns handlers -------------------------------------------

  on(actions.getAllColumns, (state, action) => {
    if (stateHasId(state, action.id)) {
      return tableAdapter.updateOne(
        { id: action.id, changes: { loadingAllColumns: true } },
        state
      );
    }
    return tableAdapter.addOne(
      {
        id: action.id,
        loadingAllColumns: true,
      },
      state
    );
  }),
  on(actions.getAllColumnsComplete, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { allColumns: action.allColumns, loadingAllColumns: false },
      },
      state
    );
  }),
  on(actions.getAllColumnsError, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { error: action.error },
      },
      state
    );
  }),

  // selected columns handlers --------------------------------------------

  on(actions.setSelectedColumns, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { settingSelectedColumns: true },
      },
      state
    );
  }),
  on(actions.setSelectedColumnsComplete, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { settingSelectedColumns: false },
      },
      state
    );
  }),
  on(actions.setSelectedColumnsError, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { error: action.error },
      },
      state
    );
  }),

  // column reorder handlers ----------------------------------------------

  on(actions.reorderColumns, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { reorderingColumns: true },
      },
      state
    );
  }),
  on(actions.reorderColumnsComplete, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { reorderingColumns: false },
      },
      state
    );
  }),
  on(actions.reorderColumnsError, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { error: action.error },
      },
      state
    );
  }),

  // column resize handlers -----------------------------------------------

  on(actions.resizeColumn, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { resizingColumns: true },
      },
      state
    );
  }),
  on(actions.resizeColumnComplete, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { resizingColumns: false },
      },
      state
    );
  }),
  on(actions.resizeColumnError, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { error: action.error },
      },
      state
    );
  }),

  // columns reset handlers -----------------------------------------------

  on(actions.resetColumnsToDefault, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { resettingColumnsToDefault: true },
      },
      state
    );
  }),
  on(actions.resetColumnsToDefaultComplete, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { resettingColumnsToDefault: false },
      },
      state
    );
  }),
  on(actions.resetColumnsToDefaultError, (state, action) => {
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: { error: action.error },
      },
      state
    );
  }),

  // clear state handler --------------------------------------------------

  on(actions.clearState, (state, action) => {
    if (!stateHasId(state, action.id)) return;
    return tableAdapter.updateOne(
      {
        id: action.id,
        changes: {
          table: undefined,
          loadingTable: undefined,

          columns: undefined,
          loadingColumns: undefined,
          settingSelectedColumns: undefined,
          reorderingColumns: undefined,
          resizingColumns: undefined,
          resettingColumnsToDefault: undefined,

          error: undefined,
        },
      },
      state
    );
  })
);

function stateHasId(state: State, id: string) {
  return (<Array<string>>state.ids).indexOf(id) !== -1;
}
